import { useEffect, useRef, useState } from 'react';
import { useApi } from '@/hooks/useApi';
import { useUserRole } from '@/hooks/useUserRole';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { isValidEmail, isPlausiblePhone, fullName } from '@/lib/validation';
import { INTEREST_TAGS } from '@/lib/interestTags';
import type { CheckResult, ProductHit } from '@/api/customers';
import '@/styles/screens.css';

interface AddCustomerModalProps {
  onClose: () => void;
  onCreated: (id: string) => void;
  onOpenExisting: (id: string) => void;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Add customer (SPEC §6d/§7) — staged capture:
 *   Step 1: email + phone → dedupe check. A likely match shows candidates
 *           (open existing, or add anyway). No collision → step 2.
 *   Step 2: name, marketing consent (off by default), interest tags, optional
 *           key date and tagged product. Saved as one flow.
 */
export function AddCustomerModal({ onClose, onCreated, onOpenExisting }: AddCustomerModalProps) {
  const api = useApi();
  const { stores } = useUserRole();
  const firstStepRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<1 | 2>(1);

  // Step 1 — contact
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState<CheckResult | null>(null);
  const [step1Error, setStep1Error] = useState<string | null>(null);

  // Step 2 — details
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [storeId, setStoreId] = useState('');
  const [optIn, setOptIn] = useState(false);
  const [tags, setTags] = useState<Set<string>>(new Set());
  const [kd, setKd] = useState<{ on: boolean; type: string; label: string; month: number; day: number }>(
    { on: false, type: 'anniversary', label: '', month: 1, day: 1 }
  );
  const [product, setProduct] = useState<ProductHit | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Adopt-and-link: set when the user picks a dedupe candidate — the new local
  // record links to the existing identity, and no Shopify record is created.
  const [link, setLink] = useState<{ shopifyGid?: string; bcContactNo?: string } | null>(null);

  useEffect(() => {
    if (stores.length && !storeId) setStoreId(stores[0].store_id);
  }, [stores, storeId]);
  useEffect(() => {
    firstStepRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const emailError = email.trim() && !isValidEmail(email) ? 'Enter a valid email.' : null;
  const phoneError = phone.trim() && !isPlausiblePhone(phone) ? 'Enter a valid phone number.' : null;
  const hasContact = !!(email.trim() || phone.trim());

  async function runCheck() {
    setStep1Error(null);
    if (!hasContact) { setStep1Error('Enter an email or phone to continue.'); return; }
    if (emailError || phoneError) return;
    setChecking(true);
    try {
      const result = await api.check(email.trim(), phone.trim());
      setCheck(result);
      if (!result.hasMatch) setStep(2);
    } catch {
      setStep1Error('Could not check for existing customers. Try again.');
    } finally {
      setChecking(false);
    }
  }

  async function save() {
    setSaving(true);
    setSaveError(null);
    try {
      const result = await api.create({
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        homeStoreId: storeId || undefined,
        optIn,
        createAnyway: true, // already dedupe-checked in step 1
        link: link ?? undefined,
      });
      if (!result.ok || !result.customer) { setSaveError('Could not save.'); return; }
      const id = result.customer.customer_id;

      // Enrich in parallel (best-effort; failures don't block the created customer).
      await Promise.allSettled([
        ...[...tags].map((t) => api.setInterest(id, t, true)),
        kd.on ? api.addKeyDate(id, { type: kd.type, label: kd.label.trim() || undefined, month: kd.month, day: kd.day }) : Promise.resolve(),
        product ? api.addFavourite(id, { sku: product.item_no, productName: product.description }) : Promise.resolve(),
      ]);
      onCreated(id);
    } catch {
      setSaveError('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="add-title" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2 className="modal-title" id="add-title">Add a customer</h2>
          <span className="wizard-step">Step {step} of 2</span>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <i className="ti ti-x" aria-hidden="true" />
          </button>
        </div>

        {step === 1 && (
          <form onSubmit={(e) => { e.preventDefault(); runCheck(); }}>
            <p className="wizard-hint">Start with their contact details — we&rsquo;ll check for an existing record first.</p>

            <div className="field">
              <label className="field-label" htmlFor="em">Email</label>
              <input id="em" ref={firstStepRef} inputMode="email" value={email}
                onChange={(e) => { setEmail(e.target.value); setCheck(null); }} />
              {emailError && <p className="field-error">{emailError}</p>}
            </div>
            <div className="field">
              <label className="field-label" htmlFor="ph">Phone</label>
              <input id="ph" inputMode="tel" value={phone}
                onChange={(e) => { setPhone(e.target.value); setCheck(null); }} />
              {phoneError && <p className="field-error">{phoneError}</p>}
            </div>

            {check?.hasMatch && (
              <div className="dedupe">
                <p className="dedupe-title">Possible existing customer{candidateCount(check) > 1 ? 's' : ''} found</p>
                {check.candidates.local.map((c) => (
                  <div className="dedupe-candidate" key={c.customer_id}>
                    <span>
                      <strong>{fullName(c.first_name, c.last_name)}</strong>
                      <span className="result-row-meta" style={{ display: 'block' }}>
                        {[c.email, c.phone].filter(Boolean).join(' · ')} · {c.store_code}
                      </span>
                    </span>
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => onOpenExisting(c.customer_id)}>Open</button>
                  </div>
                ))}
                {check.candidates.shopify.map((c) => (
                  <div className="dedupe-candidate" key={c.gid}>
                    <span><strong>{fullName(c.firstName, c.lastName)}</strong>
                      <span className="result-row-meta" style={{ display: 'block' }}>{[c.email, c.phone].filter(Boolean).join(' · ')} · Web customer</span>
                    </span>
                    <button
                      type="button" className="btn btn--sm"
                      title="Create the local record linked to this web customer — no duplicate is created"
                      onClick={() => {
                        setFirstName((v) => v || c.firstName);
                        setLastName((v) => v || c.lastName);
                        if (c.email) setEmail(c.email);
                        if (c.phone && !phone.trim()) setPhone(c.phone);
                        setLink({ shopifyGid: c.gid });
                        setStep(2);
                      }}
                    >
                      Add &amp; link
                    </button>
                  </div>
                ))}
                {check.candidates.bc.map((c) => (
                  <div className="dedupe-candidate" key={c.memberId}>
                    <span><strong>{fullName(c.firstName, c.lastName)}</strong>
                      <span className="result-row-meta" style={{ display: 'block' }}>{[c.email, c.phone].filter(Boolean).join(' · ')} · BC member</span>
                    </span>
                    <button
                      type="button" className="btn btn--sm"
                      title="Create the local record linked to this BC member"
                      onClick={() => {
                        setFirstName((v) => v || c.firstName);
                        setLastName((v) => v || c.lastName);
                        if (c.email) setEmail(c.email);
                        if (c.phone && !phone.trim()) setPhone(c.phone);
                        setLink({ bcContactNo: c.memberId });
                        setStep(2);
                      }}
                    >
                      Add &amp; link
                    </button>
                  </div>
                ))}
              </div>
            )}

            {step1Error && <p className="field-error">{step1Error}</p>}

            <div className="btn-row">
              <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
              {check?.hasMatch ? (
                <button type="button" className="btn btn--primary" onClick={() => setStep(2)}>Add as new anyway</button>
              ) : (
                <button type="submit" className="btn btn--primary" disabled={!hasContact || !!emailError || !!phoneError || checking}>
                  {checking ? 'Checking…' : 'Continue'}
                </button>
              )}
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={(e) => { e.preventDefault(); save(); }}>
            {link && (
              <p className="wizard-hint" style={{ color: 'var(--olive-deep)' }}>
                <i className="ti ti-link" aria-hidden="true" />{' '}
                Linking to the existing {link.shopifyGid ? 'web customer' : 'BC member'} — no duplicate will be created.
              </p>
            )}
            <div className="field-row">
              <div className="field">
                <label className="field-label" htmlFor="fn">First name</label>
                <input id="fn" autoFocus value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div className="field">
                <label className="field-label" htmlFor="ln">Last name</label>
                <input id="ln" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
            </div>

            <div className="field">
              <label className="field-label" htmlFor="st">Home store</label>
              <select id="st" value={storeId} onChange={(e) => setStoreId(e.target.value)} disabled={stores.length <= 1}>
                {stores.length === 0 && <option value="">No store assigned</option>}
                {stores.map((s) => <option key={s.store_id} value={s.store_id}>{s.name} ({s.code})</option>)}
              </select>
            </div>

            <div className="field">
              <label className="field-label">Interests</label>
              <div className="tag-toggles">
                {INTEREST_TAGS.map((t) => {
                  const on = tags.has(t.key);
                  return (
                    <button key={t.key} type="button" className={`tag-toggle${on ? ' is-on' : ''}`} aria-pressed={on}
                      onClick={() => setTags((prev) => { const n = new Set(prev); n.has(t.key) ? n.delete(t.key) : n.add(t.key); return n; })}>
                      <i className={`ti ${on ? 'ti-check' : 'ti-plus'}`} aria-hidden="true" />{t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <ProductPicker onPick={setProduct} picked={product} search={(q, s) => api.searchProducts(q, s)} />

            <div className="field">
              <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" style={{ width: 'auto' }} checked={kd.on} onChange={(e) => setKd({ ...kd, on: e.target.checked })} />
                Add a key date
              </label>
              {kd.on && (
                <div className="keydate-form" style={{ marginTop: 8 }}>
                  <select value={kd.type} onChange={(e) => setKd({ ...kd, type: e.target.value })}>
                    <option value="anniversary">Anniversary</option>
                    <option value="birthday">Birthday</option>
                    <option value="custom">Custom</option>
                  </select>
                  <input placeholder="Label (optional)" value={kd.label} onChange={(e) => setKd({ ...kd, label: e.target.value })} />
                  <select value={kd.month} onChange={(e) => setKd({ ...kd, month: Number(e.target.value) })} aria-label="Month">
                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                  <select value={kd.day} onChange={(e) => setKd({ ...kd, day: Number(e.target.value) })} aria-label="Day">
                    {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                  </select>
                </div>
              )}
            </div>

            <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={optIn} onChange={(e) => setOptIn(e.target.checked)} />
              Customer opted in to marketing (email &amp; SMS)
            </label>

            {saveError && <p className="field-error">{saveError}</p>}

            <div className="btn-row">
              <button type="button" className="btn btn--ghost" onClick={() => setStep(1)}>Back</button>
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save customer'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function candidateCount(c: CheckResult): number {
  return c.candidates.local.length + c.candidates.shopify.length + c.candidates.bc.length;
}

// Inline product typeahead for the optional first favourite.
function ProductPicker({ onPick, picked, search }: {
  onPick: (p: ProductHit | null) => void;
  picked: ProductHit | null;
  search: (q: string, signal?: AbortSignal) => Promise<ProductHit[]>;
}) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<ProductHit[]>([]);
  const debouncedQ = useDebouncedValue(q, 250);

  useEffect(() => {
    const term = debouncedQ.trim();
    if (picked || term.length < 2) { setHits([]); return; }
    const ctrl = new AbortController();
    search(term, ctrl.signal).then(setHits).catch(() => {});
    return () => ctrl.abort();
  }, [debouncedQ, picked, search]);

  return (
    <div className="field">
      <label className="field-label">Favourite product (optional)</label>
      {picked ? (
        <span className="chip chip--fav">
          {picked.description} <span className="chip-sku">{picked.item_no}</span>
          <button type="button" className="chip-x" aria-label="Clear" onClick={() => { onPick(null); setQ(''); }}>
            <i className="ti ti-x" aria-hidden="true" />
          </button>
        </span>
      ) : (
        <div className="picker">
          <input className="picker-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products by name or SKU" />
          {hits.length > 0 && (
            <div className="picker-results">
              {hits.map((p) => (
                <button key={p.item_no} type="button" className="picker-row" onClick={() => { onPick(p); setHits([]); }}>
                  <span>{p.description}</span><span className="picker-sku">{p.item_no}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
